import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Building2,
  Users,
  Server,
  Database,
  TrendingUp,
  CheckCircle,
  Clock,
  Monitor,
  Shield,
  Globe,
  ArrowUpRight,
  Zap,
  Activity
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SuperAdminHomeDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_tenants: 0,
    active_tenants: 0,
    trial_tenants: 0,
    total_mrr: 0,
    total_customers: 0,
    total_mikrotiks: 0
  });
  const [sessionStats, setSessionStats] = useState({
    total_active: 0,
    by_role: [],
    recent_logins_24h: 0
  });
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const tenantsRes = await fetch(`${API_URL}/api/v2/superadmin/dashboard-stats`, { headers });
        const tenantsData = await tenantsRes.json();
        
        if (tenantsData.success) {
          setStats(tenantsData.data);
        }
        
        setLoading(false);
        
        Promise.all([
          fetch(`${API_URL}/api/v2/sessions/stats`, { headers }).catch(() => null),
          fetch(`${API_URL}/api/v2/sessions?limit=5`, { headers }).catch(() => null)
        ]).then(async ([sessionsStatsRes, sessionsRes]) => {
          if (sessionsStatsRes) {
            const sessionsStatsData = await sessionsStatsRes.json().catch(() => ({}));
            if (sessionsStatsData.success) {
              setSessionStats(sessionsStatsData.data);
            }
          }
          if (sessionsRes) {
            const sessionsData = await sessionsRes.json().catch(() => ({}));
            if (sessionsData.success) {
              setRecentSessions(sessionsData.data || []);
            }
          }
        });
        
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Super Admin Dashboard</h1>
            <p className="text-sm text-slate-400 dark:text-slate-500">Monitoring seluruh sistem dan tenant ISP Billing</p>
          </div>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Tenant */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-400 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
          <Card className="relative border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-xl rounded-2xl overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10"></div>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium mb-1 dark:text-slate-500">Total Tenant</p>
                  <p className="text-3xl font-bold text-white">{stats.total_tenants}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-blue-400">
                <ArrowUpRight className="w-3 h-3" />
                <span>Semua tenant terdaftar</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Tenant */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
          <Card className="relative border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-xl rounded-2xl overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full -mr-10 -mt-10"></div>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium mb-1 dark:text-slate-500">Tenant Aktif</p>
                  <p className="text-3xl font-bold text-white">{stats.active_tenants}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-emerald-400">
                <Zap className="w-3 h-3" />
                <span>Berlangganan aktif</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trial Tenant */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-400 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
          <Card className="relative border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-xl rounded-2xl overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full -mr-10 -mt-10"></div>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium mb-1 dark:text-slate-500">Masa Trial</p>
                  <p className="text-3xl font-bold text-white">{stats.trial_tenants}</p>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-400" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-amber-400">
                <Clock className="w-3 h-3" />
                <span>Dalam periode uji coba</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Revenue */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-violet-400 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
          <Card className="relative border-0 bg-gradient-to-br from-slate-800 to-slate-800/50 backdrop-blur-xl rounded-2xl overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-violet-500/10 rounded-full -mr-10 -mt-10"></div>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium mb-1 dark:text-slate-500">Monthly Revenue</p>
                  <p className="text-3xl font-bold text-white">
                    {stats.total_mrr >= 1000000 
                      ? `${(stats.total_mrr / 1000000).toFixed(1)}M` 
                      : `${(stats.total_mrr / 1000).toFixed(0)}K`}
                  </p>
                </div>
                <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-violet-400" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-violet-400">
                <TrendingUp className="w-3 h-3" />
                <span>Pendapatan bulanan</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Database Stats & Sessions Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Database Stats */}
        <Card className="border-0 bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur-xl rounded-2xl overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Database Stats</h3>
                  <p className="text-xs text-slate-400 dark:text-slate-500">MySQL Server - 103.179.252.149</p>
                </div>
              </div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
                Connected
              </span>
            </div>
            
            <div className="grid grid-cols-5 gap-3">
              <div className="bg-slate-700/50 rounded-xl p-3 text-center border border-slate-600/30">
                <p className="text-2xl font-bold text-white">{stats.total_customers || 0}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Customers</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-3 text-center border border-slate-600/30">
                <p className="text-2xl font-bold text-white">{stats.total_invoices || 0}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Invoices</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-3 text-center border border-slate-600/30">
                <p className="text-2xl font-bold text-white">{stats.total_mikrotiks || 0}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">MikroTiks</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-3 text-center border border-slate-600/30">
                <p className="text-2xl font-bold text-white">{stats.total_branches || 0}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Branches</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-3 text-center border border-emerald-500/30">
                <p className="text-2xl font-bold text-emerald-400">{stats.pppoe_online || 0}</p>
                <p className="text-xs text-emerald-400/80 mt-1">PPPoE Online</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card className="border-0 bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur-xl rounded-2xl overflow-hidden" data-testid="sessions-widget">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Active Sessions</h3>
                  <p className="text-xs text-slate-400 dark:text-slate-500">{sessionStats.total_active} user aktif login</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white"
                onClick={() => navigate('/app/session-management')}
                data-testid="view-all-sessions-btn"
              >
                Lihat Semua
              </Button>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl p-4 text-center border border-red-500/20">
                <Shield className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {sessionStats.by_role?.find(r => r.role === 'super_admin')?.count || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Super Admin</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-4 text-center border border-emerald-500/20">
                <Users className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {sessionStats.by_role?.find(r => r.role === 'owner')?.count || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Owner</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 text-center border border-blue-500/20">
                <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{sessionStats.recent_logins_24h || 0}</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Login 24 Jam</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Logins & Quick Actions Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Logins */}
        <Card className="border-0 bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur-xl rounded-2xl overflow-hidden">
          <CardContent className="p-5">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4 text-cyan-400" />
              Login Terbaru
            </h3>
            <div className="space-y-3">
              {recentSessions.length > 0 ? (
                recentSessions.slice(0, 4).map((session, idx) => (
                  <div 
                    key={session.id || idx} 
                    className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl border border-slate-600/20 hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        session.role === 'super_admin' 
                          ? 'bg-red-500/20 border border-red-500/30' 
                          : session.role === 'owner' 
                          ? 'bg-emerald-500/20 border border-emerald-500/30' 
                          : 'bg-blue-500/20 border border-blue-500/30'
                      }`}>
                        {session.role === 'super_admin' ? (
                          <Shield className="w-5 h-5 text-red-400" />
                        ) : (
                          <Users className="w-5 h-5 text-emerald-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{session.username}</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">{session.role?.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400 flex items-center gap-1 dark:text-slate-500">
                        <Globe className="w-3 h-3" />
                        {session.ip_address || '-'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-slate-400 py-8 dark:text-slate-500">
                  <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Belum ada login terbaru</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur-xl rounded-2xl overflow-hidden">
          <CardContent className="p-5">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <a
                href="/app/super-admin"
                className="group p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 hover:from-blue-500/20 hover:to-blue-600/10 border border-blue-500/20 rounded-xl transition-all hover:scale-[1.02]"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-3 group-hover:bg-blue-500/30 transition-colors">
                  <Building2 className="w-6 h-6 text-blue-400" />
                </div>
                <p className="text-sm font-medium text-white">Manage Tenants</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Kelola semua tenant</p>
              </a>
              <a
                href="/app/tenant-billing"
                className="group p-4 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 hover:from-emerald-500/20 hover:to-emerald-600/10 border border-emerald-500/20 rounded-xl transition-all hover:scale-[1.02]"
              >
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-3 group-hover:bg-emerald-500/30 transition-colors">
                  <TrendingUp className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-sm font-medium text-white">Tenant Billing</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Tagihan & pembayaran</p>
              </a>
              <a
                href="/app/superadmin-vpn-management"
                className="group p-4 bg-gradient-to-br from-violet-500/10 to-violet-600/5 hover:from-violet-500/20 hover:to-violet-600/10 border border-violet-500/20 rounded-xl transition-all hover:scale-[1.02]"
              >
                <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center mb-3 group-hover:bg-violet-500/30 transition-colors">
                  <Server className="w-6 h-6 text-violet-400" />
                </div>
                <p className="text-sm font-medium text-white">VPN Management</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Kelola akses VPN</p>
              </a>
              <a
                href="/app/subscription-plans"
                className="group p-4 bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 hover:from-cyan-500/20 hover:to-cyan-600/10 border border-cyan-500/20 rounded-xl transition-all hover:scale-[1.02]"
              >
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-3 group-hover:bg-cyan-500/30 transition-colors">
                  <Users className="w-6 h-6 text-cyan-400" />
                </div>
                <p className="text-sm font-medium text-white">Plans & Pricing</p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Paket langganan</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminHomeDashboard;
